import type {
  ApplyTemplateMessage,
  PublishFormData,
  RequestPublishFormDataMessage,
  WildmokaIframeApiInputMessage,
  WildmokaIframeApiOutputMessage,
} from './messages';

export class WildmokaIframeApi extends EventTarget {
  #iframe: HTMLIFrameElement | undefined;
  #targetOrigin: string | undefined;

  init(iframe: HTMLIFrameElement) {
    this.#iframe = iframe;
  }

  setTargetOrigin(baseUrl: string) {
    // `ui_base_url` may include a trailing slash or path; `event.origin` never does.
    this.#targetOrigin = new URL(baseUrl).origin;
  }

  #getIframe(): HTMLIFrameElement {
    if (!this.#iframe) {
      throw new Error('Iframe is not initialized');
    }
    return this.#iframe;
  }

  #getTargetOrigin(): string {
    if (!this.#targetOrigin) {
      throw new Error('Target origin is not set');
    }
    return this.#targetOrigin;
  }

  #postMessage(message: WildmokaIframeApiOutputMessage) {
    const iframe = this.#getIframe();
    const targetOrigin = this.#getTargetOrigin();
    const contentWindow = iframe.contentWindow;

    if (!contentWindow) {
      throw new Error('Iframe contentWindow is not available.');
    }

    contentWindow.postMessage(message, targetOrigin);
  }

  getPublishFormData(): Promise<PublishFormData> {
    return new Promise((resolve, reject) => {
      const iframe = this.#getIframe();
      const targetOrigin = this.#getTargetOrigin();

      if (!iframe.contentWindow) {
        reject(new Error('Iframe contentWindow is not available.'));
        return;
      }

      const handleMessage = (
        event: MessageEvent<WildmokaIframeApiInputMessage>,
      ) => {
        if (
          event.origin !== targetOrigin ||
          event.source !== iframe.contentWindow
        ) {
          return;
        }

        const { data } = event;
        // Since it's a generic window message, we can't trust that the type will be correct.
        if (!data) {
          return;
        }
        if (
          data.messageType !== 'publish-panel' ||
          data.operation !== 'sendPublishFormData'
        ) {
          return;
        }

        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutId);

        resolve(data.payload);
      };

      window.addEventListener('message', handleMessage);

      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        reject(
          new Error('Timed out waiting for iframe to send publish form data.'),
        );
      }, 5000);

      const message: RequestPublishFormDataMessage = {
        messageType: 'publish-panel',
        operation: 'requestPublishFormData',
      };

      this.#postMessage(message);
    });
  }

  setTemplateId(
    templateId: string | null,
    metadataValues: ApplyTemplateMessage['payload']['metadata'],
  ) {
    const message: ApplyTemplateMessage = {
      messageType: 'publish-panel',
      operation: 'applyTemplate',
      // We default to send -1 as the templateId, to indicate a user resetting the selection.
      payload: {
        templateId: templateId ?? -1,
        metadata: metadataValues,
      },
    };

    this.#postMessage(message);
  }
}
